import {
  ALTERNATIVE_DATES_SESSION,
  ALTERNATIVE_DATES_POLL,
} from '@web-platform/shared-universal-types/src/network/requestTypes';
import { mapMoney } from '@web-platform/shared-universal-utils-frontend/src/helpers/formatMoney';

import type { SkyscannerRadarAlternativedatesV1AlternativeDates } from '@skyscanner-internal/radar-web-http-client';
import type { AlternateDateItem } from '@web-platform/shared-universal-constants/src/alternativeDates';
import type {
  MutationType,
  QueryType,
} from '@web-platform/shared-universal-types/src/network/requestTypes';
import type { AlternativeDatesPayloadResponseSuccess } from '@web-platform/shared-universal-types/src/network/types';
import type { Action } from '@web-platform/shared-universal-types/src/redux/acorn/actions/actions-acorn-types';
import type { AlternativeDatesState } from '@web-platform/shared-universal-types/src/redux/acorn/state/flightsDayView/alternativeDates';
import type { PayloadResponse } from '@web-platform/shared-universal-types/src/redux/actions/actions-shared-types';
import type { Nullable } from '@web-platform/shared-universal-types-utils/src/utils';

type AlternativeDate = SkyscannerRadarAlternativedatesV1AlternativeDates.AlternativeDate;

const getInitialState = (): AlternativeDatesState => ({
  alternativeDates: null,
});

const mapAlternativeDateToAlternateDateItem = (
  alternativeDates: AlternativeDate[],
): AlternateDateItem[] =>
  alternativeDates.map((date: AlternativeDate) => ({
    departureDate: date.departureDate ?? '',
    returnDate: date.returnDate,
    availability: date.availability || 'AVAILABILITY_UNSPECIFIED',
    directAvailability: date.directAvailability || 'AVAILABILITY_UNSPECIFIED',
    cheapestPrice: mapMoney(date.cheapestPrice),
    cheapestDirectPrice: mapMoney(date.cheapestDirectPrice),
    cheapestNonDirectPrice: mapMoney(date.cheapestNonDirectPrice),
    priceCategory: date.priceCategory || 'PRICE_CATEGORY_UNSPECIFIED',
    directPriceCategory: date.directPriceCategory || 'PRICE_CATEGORY_UNSPECIFIED',
  }));

const mapAlternativeDatesResponse = (
  state: AlternativeDatesState,
  response: AlternativeDatesPayloadResponseSuccess,
): AlternativeDatesState => {
  const mappedResponse: AlternateDateItem[] = mapAlternativeDateToAlternateDateItem(
    response.body.alternativeDates ?? [],
  );
  return {
    ...state,
    alternativeDates: state.alternativeDates?.length
      ? state.alternativeDates.concat(mappedResponse)
      : mappedResponse,
  };
};

const isAlternativeDatesResponseSuccess = (
  response: PayloadResponse,
  requestType: QueryType | MutationType,
): response is AlternativeDatesPayloadResponseSuccess => {
  const alternativeDatesRequestTypes: Array<QueryType | MutationType> = [
    ALTERNATIVE_DATES_SESSION,
    ALTERNATIVE_DATES_POLL,
  ];

  if (!alternativeDatesRequestTypes.includes(requestType)) return false;
  if (response.error) return false;
  return 'alternativeDates' in response.body;
};

const alternativeDatesReducer = (
  state: AlternativeDatesState = getInitialState(),
  action: Action,
): AlternativeDatesState => {
  switch (action.type) {
    case 'QUERY_RESPONSE_RECEIVED':
    case 'MUTATION_RESPONSE_RECEIVED': {
      if (isAlternativeDatesResponseSuccess(action.payload.response, action.payload.type)) {
        return mapAlternativeDatesResponse(state, action.payload.response);
      }
      return state;
    }
    default:
      return state;
  }
};

export const getAlternativeDates = (state: AlternativeDatesState): Nullable<AlternateDateItem[]> =>
  state.alternativeDates;

export default alternativeDatesReducer;
